import { z } from "zod";

/**
 * The question types ZenForge can generate, as `question_type.code` in the
 * main app.
 *
 * Taken from that app's ASSESSMENT_TYPE enum, minus the two it defines that
 * are not question-bank types: `jumbled_words` and `click_to_recall` are
 * in-lesson interactives stored in assessment_content, a different table
 * reached through a different push path (see main-app/assessment.ts).
 *
 * Note `question_answer` — one code covering short answer, long answer and
 * exercises alike. The main app's questionBankV2.service.ts carries a display
 * map naming `short_answer` and `answer_in_detail` instead, but no question in
 * the database uses either: sampling forty chapters returns only the four
 * codes below. That map is stale; the enum and the data agree with each other.
 *
 * This list drives the category form's dropdown. It is deliberately NOT
 * enforced on the stored value — `questionAssessmentType` stays a free string
 * so the main app can add a type without ZenForge being redeployed, and
 * validateQuestionsForType passes anything it does not recognise.
 */
export const QUESTION_TYPES = [
  { code: "mcq", label: "MCQ" },
  { code: "fill_in_the_blanks", label: "Fill in the Blanks" },
  { code: "true_false", label: "True / False" },
  { code: "match_the_following", label: "Match the Following" },
  { code: "question_answer", label: "Question & Answer (short, long, exercises)" },
] as const;

export type QuestionTypeCode = (typeof QUESTION_TYPES)[number]["code"];

/**
 * The shape a Book In Questions category must generate, and the duplicate
 * check applied to it before publishing.
 *
 * Lives in @zenforge/shared because three places need it and must not
 * disagree: the server validates generated JSON against it before pushing,
 * the review UI renders questions by it, and the category's outputSchema (the
 * JSON schema actually sent to the model) is derived from it.
 */

/**
 * One MCQ option.
 *
 * `optionText`, not `text`: the main app reads that exact key when resolving
 * images embedded in question HTML, and a differently-named field is silently
 * dropped on the way in.
 */
export const questionOptionSchema = z.object({
  optionText: z.string().min(1, "An option cannot be empty"),
  isCorrect: z.boolean(),
  explanation: z.string().nullable().optional(),
});

/**
 * One generated question, mapping 1:1 onto the main app's
 * BulkQuestionContentItemDTO.
 *
 * Only `question` is required. Which of the rest apply depends on the
 * assessment type — options for mcq, columnA/columnB for
 * match_the_following, answer alone for question_answer — so requiring any of
 * them here would reject a legitimately-shaped non-MCQ question. The
 * per-type rules are enforced by validateQuestionsForType below, which knows
 * the category's assessment type; this schema only enforces what is true of
 * every question regardless of type.
 */
export const generatedQuestionSchema = z.object({
  questionNo: z.number().int().nullable().optional(),
  question: z.string().min(1, "Question text is required"),
  variants: z.array(z.string()).nullable().optional(),
  options: z.array(questionOptionSchema).nullable().optional(),
  columnA: z.array(z.string()).nullable().optional(),
  columnB: z.array(z.string()).nullable().optional(),
  answer: z.union([z.string(), z.array(z.string())]).nullable().optional(),
  explanation: z.string().nullable().optional(),
});

export type QuestionOption = z.infer<typeof questionOptionSchema>;
export type GeneratedQuestion = z.infer<typeof generatedQuestionSchema>;

/**
 * The generated payload as a whole.
 *
 * Accepts either a bare array or `{ questions: [...] }` — models wrap a
 * top-level array in an object often enough that rejecting it would fail
 * generations that are otherwise perfectly correct. Both normalise to an
 * array via parseGeneratedQuestions.
 */
export const generatedQuestionsSchema = z.union([
  z.array(generatedQuestionSchema),
  z.object({ questions: z.array(generatedQuestionSchema) }),
  z.object({ items: z.array(generatedQuestionSchema) }),
]);

export interface ParseQuestionsResult {
  questions: GeneratedQuestion[];
  /** Human-readable reason, when parsing or validation failed. */
  error: string | null;
}

/**
 * Parses and validates a generated JSON body into questions.
 *
 * Returns the failure rather than throwing, because both callers want to
 * report it rather than crash: the review UI renders it as a notice, and the
 * push path turns it into a 400 naming what was wrong.
 */
export function parseGeneratedQuestions(body: string | unknown): ParseQuestionsResult {
  let raw: unknown = body;

  if (typeof body === "string") {
    try {
      raw = JSON.parse(body);
    } catch {
      return { questions: [], error: "Generated content is not valid JSON." };
    }
  }

  const parsed = generatedQuestionsSchema.safeParse(raw);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    const path = first?.path.join(".");
    return {
      questions: [],
      error: path
        ? `Generated questions do not match the expected schema (${path}: ${first?.message}).`
        : `Generated questions do not match the expected schema (${first?.message ?? "unknown"}).`,
    };
  }

  const questions = Array.isArray(parsed.data)
    ? parsed.data
    : "questions" in parsed.data
      ? parsed.data.questions
      : parsed.data.items;

  if (questions.length === 0) {
    return { questions: [], error: "No questions were generated." };
  }

  return { questions, error: null };
}

/**
 * Per-assessment-type rules, checked before publishing.
 *
 * These exist because the main app accepts a structurally-valid but
 * semantically empty question without complaint — an mcq with no options is
 * created happily and is then useless to a student, with nothing in the
 * response indicating anything went wrong. Catching it here means the
 * reviewer is told, rather than the question quietly landing broken.
 *
 * Unknown assessment types pass with only the universal checks: the main
 * app's question_type table gains rows without ZenForge redeploying, and a
 * new type must not become unpublishable just because this list has not
 * caught up.
 */
export function validateQuestionsForType(
  questions: GeneratedQuestion[],
  assessmentType: string,
): string[] {
  const problems: string[] = [];

  questions.forEach((question, index) => {
    const label = `Question ${index + 1}`;

    switch (assessmentType) {
      case "mcq": {
        const options = question.options ?? [];
        if (options.length < 2) {
          problems.push(`${label}: an MCQ needs at least two options.`);
        } else if (!options.some((option) => option.isCorrect)) {
          problems.push(`${label}: no option is marked correct.`);
        }
        break;
      }

      case "true_false": {
        // Either shape is accepted: some prompts produce two options, others
        // a bare "True"/"False" answer. Both are publishable.
        const hasOptions = (question.options ?? []).length >= 2;
        const hasAnswer = typeof question.answer === "string" && question.answer.trim().length > 0;
        if (!hasOptions && !hasAnswer) {
          problems.push(`${label}: a true/false question needs an answer or two options.`);
        }
        break;
      }

      case "match_the_following": {
        const a = question.columnA ?? [];
        const b = question.columnB ?? [];
        if (a.length === 0 || b.length === 0) {
          problems.push(`${label}: a match question needs both columnA and columnB.`);
        } else if (a.length !== b.length) {
          problems.push(
            `${label}: columnA has ${a.length} entries but columnB has ${b.length} — they must match.`,
          );
        }
        break;
      }

      case "fill_in_the_blanks":
      case "question_answer": {
        const hasAnswer = Array.isArray(question.answer)
          ? question.answer.length > 0
          : typeof question.answer === "string" && question.answer.trim().length > 0;
        if (!hasAnswer) problems.push(`${label}: an answer is required.`);
        break;
      }

      default:
        // Unknown type — universal checks only, which the schema already did.
        break;
    }
  });

  return problems;
}

/**
 * Reduces a question to the form the duplicate check compares.
 *
 * The point is to catch a question the model reproduced from the book back
 * despite being told not to, in a form trivially different from the original:
 * different case, a full stop added or dropped, "Fill in the blank:" prefixed,
 * curly quotes instead of straight ones, a doubled space.
 *
 * Deliberately NOT a similarity score. A normalised string equality check is
 * explainable to a reviewer ("this is the same question as book-back #4") and
 * has no threshold to tune. It will not catch a genuine rewording — that is a
 * known and accepted limit, and is what the prompt-level exclusion list is
 * for.
 *
 * Order matters: HTML is stripped before punctuation, or tag brackets would
 * survive as text.
 */
export function normalizeQuestionText(text: string): string {
  return (
    text
      // Generated question text may be HTML — the main app stores it that way.
      .replace(/<[^>]*>/g, " ")
      // &nbsp; in particular survives tag-stripping and is invisible.
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .toLowerCase()
      // Curly quotes and dashes: a model and a textbook rarely agree on these,
      // and the difference is never meaningful.
      .replace(/[‘’‛′]/g, "'")
      .replace(/[“”‟″]/g, '"')
      .replace(/[‐-―]/g, "-")
      // Leading enumeration the book back has and the model does not, or the
      // reverse: "4.", "(a)", "iii)", "a)".
      //
      // Two alternatives rather than one with optional brackets: a bare
      // "a. text" must consume the letter, while "(a) text" must consume the
      // brackets too — a single pattern with an optional open paren strips
      // the bracket and leaves the letter behind as a word.
      .replace(/^\s*(?:\([0-9a-z]+\)|[0-9]+[.)]|[a-z][.)])\s+/i, "")
      // All remaining punctuation goes: it is exactly the class of difference
      // that must not make two identical questions look distinct.
      .replace(/[^\p{L}\p{N}\s]/gu, " ")
      .replace(/\s+/g, " ")
      .trim()
  );
}

/**
 * The text a question is de-duplicated on.
 *
 * For almost every type this is the question itself. Match the Following is
 * the exception: its stem is boilerplate ("Match the terms with their correct
 * details:") and is identical across every set, so keying on it would make
 * the second and third set of a generation look like repeats of the first and
 * silently drop them. What actually distinguishes one Match set from another
 * is the items being matched, so columnA is folded into the key.
 *
 * columnB is deliberately excluded: the same pairs shuffled into a different
 * order are the same question, and including it would let a reshuffle pass as
 * new.
 */
function duplicateKeySource(question: GeneratedQuestion): string {
  const columnA = question.columnA ?? [];
  if (columnA.length > 0) {
    // Sorted so the same set of items keys identically however the model
    // happened to order them.
    return `${question.question} ${[...columnA].sort().join(" ")}`;
  }
  return question.question;
}

export interface DuplicateMatch {
  question: GeneratedQuestion;
  /** The book-back question it collided with, as originally written. */
  matched: string;
}

export interface DuplicateCheckResult {
  unique: GeneratedQuestion[];
  duplicates: DuplicateMatch[];
}

/**
 * Splits generated questions into those safe to publish and those that
 * duplicate a book-back question.
 *
 * Also catches duplicates WITHIN the generated set — a model asked for
 * fifteen questions sometimes returns the same one twice, and publishing both
 * is no better than publishing a book-back repeat. The first occurrence wins.
 *
 * An empty book-back list is not an error: a chapter may genuinely have no
 * book-back questions entered, in which case nothing can duplicate and every
 * question passes.
 */
export function partitionDuplicates(
  questions: GeneratedQuestion[],
  bookBackQuestions: string[],
): DuplicateCheckResult {
  // Normalised -> original, so a match can be reported in the words the
  // reviewer will recognise rather than the normalised form.
  const bookBack = new Map<string, string>();
  for (const text of bookBackQuestions) {
    const key = normalizeQuestionText(text);
    if (key) bookBack.set(key, text);
  }

  const unique: GeneratedQuestion[] = [];
  const duplicates: DuplicateMatch[] = [];
  const seen = new Map<string, string>();

  for (const question of questions) {
    const key = normalizeQuestionText(duplicateKeySource(question));

    // A question that normalises to nothing (pure punctuation) is not a
    // duplicate of anything, but it is not publishable either — schema
    // validation already rejected empty text, so this can only be exotic
    // input. Treat it as unique and let the main app judge it.
    if (!key) {
      unique.push(question);
      continue;
    }

    const bookBackMatch = bookBack.get(key);
    if (bookBackMatch !== undefined) {
      duplicates.push({ question, matched: bookBackMatch });
      continue;
    }

    const earlier = seen.get(key);
    if (earlier !== undefined) {
      duplicates.push({ question, matched: earlier });
      continue;
    }

    seen.set(key, question.question);
    unique.push(question);
  }

  return { unique, duplicates };
}

/**
 * The JSON schema handed to the model as a category's outputSchema.
 *
 * Derived from the zod schema above in intent, written out here because the
 * provider sends a JSON Schema document, not a zod object. Kept as a function
 * of assessmentType so an MCQ category's schema actually says "options are
 * required" rather than describing every field of every question type and
 * leaving the model to guess which apply.
 */
export function questionOutputSchema(assessmentType: string): Record<string, unknown> {
  const optionSchema = {
    type: "object",
    properties: {
      optionText: { type: "string" },
      isCorrect: { type: "boolean" },
      explanation: { type: ["string", "null"] },
    },
    required: ["optionText", "isCorrect"],
    additionalProperties: false,
  };

  const properties: Record<string, unknown> = {
    questionNo: { type: ["integer", "null"] },
    question: { type: "string" },
    explanation: { type: ["string", "null"] },
  };
  const required = ["question"];

  switch (assessmentType) {
    case "mcq":
      properties.options = { type: "array", items: optionSchema, minItems: 2 };
      required.push("options");
      break;

    case "true_false":
      // Lowercase, and one casing chosen deliberately: the main app stores
      // `answer` as an opaque string and never compares it, so whatever is
      // written here is what a student-facing renderer will have to match on.
      // Two spellings in the same column would break such a comparison for
      // half the rows.
      properties.answer = { type: "string", enum: ["true", "false"] };
      required.push("answer");
      break;

    case "match_the_following":
      properties.columnA = { type: "array", items: { type: "string" } };
      properties.columnB = { type: "array", items: { type: "string" } };
      properties.answer = { type: "array", items: { type: "string" } };
      required.push("columnA", "columnB");
      break;

    default:
      // fill_in_the_blanks, question_answer and anything the main app adds
      // later: a question and its answer.
      properties.answer = { type: "string" };
      required.push("answer");
      break;
  }

  return {
    type: "object",
    properties: {
      questions: {
        type: "array",
        items: { type: "object", properties, required, additionalProperties: false },
      },
    },
    required: ["questions"],
    additionalProperties: false,
  };
}
